import React from "react";

import { LinkRounded, Share } from "@mui/icons-material";
import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Avatar,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { HelpBox } from "@/components/atoms";
import { Activation, ActivationStatus } from "@/types";
import {
  activationStatusColorMap,
  activationStatusDescription,
  isActivationExpired,
  renderDateRange,
} from "@/utils";

interface ActivationCardProps {
  activation: Activation;
  onEdit: (activation: Activation) => void;
  onShare: () => void;
}

const ActivationCard: React.FC<ActivationCardProps> = ({
  activation,
  onEdit,
  onShare,
}) => {
  const theme = useTheme();
  const sharedOverlay = alpha(theme.palette.primary.main, 0.051);

  const isSharedActivation = activation.shared;
  const numSharedWith =
    (activation.shared_with && activation.shared_with.length) || 0;

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "220px",
        backgroundImage: isSharedActivation
          ? `linear-gradient(${sharedOverlay}, ${sharedOverlay})`
          : "var(--Paper-overlay)",
      }}
    >
      <CardActionArea onClick={() => onEdit(activation)}>
        <CardHeader
          avatar={
            isSharedActivation ? (
              <HelpBox title={"This activation is shared with you"}>
                <LinkRounded color="primary" />
              </HelpBox>
            ) : activation.shared_with && numSharedWith > 0 ? (
              <HelpBox
                title={`This activation is shared with ${numSharedWith} user${
                  numSharedWith > 1 ? "s" : ""
                }`}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    background: alpha(theme.palette.info.main, 0.5),
                    width: 20,
                    height: 20,
                    fontSize: "12px",
                    m: "2px",
                  }}
                >
                  {numSharedWith}
                </Avatar>
              </HelpBox>
            ) : (
              <Box height={24} />
            )
          }
          sx={{ p: 1, pb: 0 }}
        />
        <CardContent
          sx={{
            height: "122px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-around",
            alignItems: "center",
            pt: 0,
            pb: 0,
            "&:last-child": { pb: 0 },
          }}
        >
          <Typography
            variant="h6"
            textAlign="center"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
            }}
          >
            {activation.campaign_name}
          </Typography>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "1",
                WebkitBoxOrient: "vertical",
              }}
            >
              {activation.platform}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {`${activation.audiences.length} Segment${
                activation.audiences.length > 1 ? "s" : ""
              }`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {renderDateRange(
                activation.campaign_date_start,
                activation.campaign_date_end
              )}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
      <CardActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          pl: 2,
        }}
      >
        <HelpBox
          title={
            activationStatusDescription[
              (isActivationExpired(activation)
                ? "complete"
                : activation.status) as ActivationStatus
            ]
          }
          placement="bottom"
        >
          <Typography
            variant="body2"
            color={
              activationStatusColorMap[
                (isActivationExpired(activation)
                  ? "complete"
                  : activation.status) as ActivationStatus
              ]
            }
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            {isActivationExpired(activation) ? "complete" : activation.status}
          </Typography>
        </HelpBox>
        <HelpBox title="Share this Activation">
          <IconButton
            size="small"
            color="primary"
            onClick={onShare}
            disabled={isSharedActivation || (!isActivationExpired(activation) && !['ready', 'complete'].includes(activation.status))}
          >
            <Share fontSize="small"/>
          </IconButton>
        </HelpBox>
      </CardActions>
    </Card>
  );
};

export default ActivationCard;
